import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Button,
  Switch,
  Space,
  Typography,
  message,
  Spin,
  Empty,
  Alert,
  Row,
  Col,
} from 'antd';
import {
  CreditCardOutlined,
  SaveOutlined,
  BankOutlined,
  WalletOutlined,
  QrcodeOutlined,
  DollarOutlined,
  GlobalOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PaymentMethodsService, {
  PaymentMethodCatalog,
  PaymentMethodItem,
} from '../../services/paymentMethods';

const { Text, Paragraph } = Typography;

// 钱包类方式（用于内部图标判断，绝不显示底层 provider）
const WALLET_METHODS = new Set([
  'apple_pay',
  'google_pay',
  'amazon_pay',
  'link',
  'wechat_pay',
  'alipay',
  'paypal',
]);

// 根据方式代码 / 集成类型选择中性图标（provider 仅作内部判断，不渲染文案）
const methodIcon = (item: PaymentMethodItem): React.ReactNode => {
  const m = item.method;
  const t = item.integration_type;
  const color = '#2f6bff';
  const style = { color, fontSize: 18 };

  if (t === 'crypto') return <DollarOutlined style={style} />;
  if (t === 'qr') return <QrcodeOutlined style={style} />;
  if (WALLET_METHODS.has(m) || m.endsWith('_pay')) {
    return <WalletOutlined style={style} />;
  }
  if (m.includes('bank') || m.endsWith('_debit') || m === 'sepa_debit') {
    return <BankOutlined style={style} />;
  }
  if (m === 'card') return <CreditCardOutlined style={style} />;
  if (t === 'redirect') return <GlobalOutlined style={style} />;
  if (t === 'stripe_element') return <CreditCardOutlined style={style} />;
  return <MobileOutlined style={style} />;
};

const PaymentMethodsTab: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState<PaymentMethodCatalog | null>(null);

  // 自动模式总开关：true=系统按已启用方式自动展示
  const [autoMode, setAutoMode] = useState(false);
  // 手动模式下每个方式的启用状态
  const [enabledMap, setEnabledMap] = useState<Record<string, boolean>>({});

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const c = await PaymentMethodsService.getCatalog();
      setCatalog(c);
      const map: Record<string, boolean> = {};
      c.methods.forEach((m) => {
        map[m.method] = !!m.enabled;
      });
      setEnabledMap(map);
      // 若后端未返回任何明确启用项，则视为自动模式
      const anyEnabled = c.methods.some((m) => m.enabled);
      setAutoMode(!anyEnabled);
    } catch (e) {
      message.error(t('paymentMethods.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const toggleMethod = (method: string, checked: boolean) => {
    setEnabledMap((prev) => ({ ...prev, [method]: checked }));
  };

  const handleSave = async () => {
    if (!catalog) return;
    let payload: string[];
    if (autoMode) {
      payload = ['automatic'];
    } else {
      payload = catalog.methods
        .filter((m) => enabledMap[m.method])
        .map((m) => m.method);
    }
    setSaving(true);
    try {
      await PaymentMethodsService.updateCheckoutMethods(payload);
      message.success(
        autoMode
          ? t('paymentMethods.savedAuto')
          : t('paymentMethods.saved')
      );
    } catch (e) {
      message.error(t('paymentMethods.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!catalog) {
    return <Empty description={t('paymentMethods.cannotLoad')} />;
  }

  const hasMethods = catalog.methods.length > 0;

  const renderMethodRow = (item: PaymentMethodItem) => (
    <Row
      key={item.method}
      align="middle"
      justify="space-between"
      gutter={16}
      style={{
        padding: '14px 0',
        borderBottom: '1px solid #f0f0f0',
        opacity: autoMode ? 0.55 : 1,
      }}
    >
      <Col flex="auto">
        <Space size={12} align="center">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 8,
              background: '#f0f5ff',
            }}
          >
            {methodIcon(item)}
          </span>
          <Text strong>{item.label || item.method}</Text>
        </Space>
      </Col>
      <Col>
        <Switch
          checkedChildren={t('paymentMethods.on')}
          unCheckedChildren={t('paymentMethods.off')}
          disabled={autoMode}
          checked={!!enabledMap[item.method]}
          onChange={(checked) => toggleMethod(item.method, checked)}
        />
      </Col>
    </Row>
  );

  return (
    <div>
      <Card
        title={
          <Space>
            <CreditCardOutlined />
            <span>{t('paymentMethods.title')}</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Paragraph type="secondary">{t('paymentMethods.intro')}</Paragraph>

        {/* 自动模式总开关 */}
        <Card size="small" type="inner" style={{ marginBottom: 16 }}>
          <Row align="middle" justify="space-between" gutter={16}>
            <Col flex="auto">
              <Space direction="vertical" size={2}>
                <Text strong>{t('paymentMethods.autoMode')}</Text>
                <Text type="secondary">
                  {t('paymentMethods.autoModeDesc')}
                </Text>
              </Space>
            </Col>
            <Col>
              <Switch
                checked={autoMode}
                onChange={setAutoMode}
                checkedChildren={t('paymentMethods.auto')}
                unCheckedChildren={t('paymentMethods.manual')}
              />
            </Col>
          </Row>
        </Card>

        {autoMode && (
          <Alert
            type="info"
            showIcon
            message={t('paymentMethods.autoModeAlert')}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* 扁平方式列表 */}
        {!hasMethods ? (
          <Empty
            description={t('paymentMethods.empty')}
            style={{ margin: '24px 0' }}
          />
        ) : (
          <div style={{ marginBottom: 8 }}>
            {catalog.methods.map(renderMethodRow)}
          </div>
        )}

        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
          style={{ marginTop: 16 }}
        >
          {t('common.save')}
        </Button>
      </Card>
    </div>
  );
};

export default PaymentMethodsTab;
